/* Expandable "+" toggle for showing prior versions of a signed clinical record.
   KISS: Fetches versions from /clinical-records/{kind}/{id}/versions on first expand.
   Latest version stays at the top (in the current row); prior versions render below,
   each with its own View button that opens the printable render with ?version=X. */
import { useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, FileText, Pencil } from "lucide-react";

export function VersionAccordionCell({ record, kind, testId }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState(null);
  const versionCount = record?.version_count || 1;
  const hasAmendments = versionCount > 1;

  const toggle = async () => {
    if (!hasAmendments) return;
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (versions) return;
    setLoading(true);
    try {
      const r = await api.get(`/clinical-records/${kind}/${record.id}/versions`);
      setVersions(r.data.versions || []);
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  if (!hasAmendments) return null;

  return (
    <button type="button" onClick={toggle}
      data-testid={testId || `version-toggle-${record.id}`}
      title={`${versionCount} versions. Click to see prior amendments.`}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#EDE7DC] text-[#4E342E] hover:bg-[#DBCDB6]">
      {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
    </button>
  );
}

/**
 * Full expandable row wrapper: renders the primary <tr> plus a versioned accordion <tr>
 * when expanded. Because keeping expansion state per row within a shared toggle is
 * awkward without lifting state, this helper composes both into a single tuple.
 *
 * Usage:
 *   <VersionAccordion record={n} kind="progress_note" colSpan={totalCols}
 *     renderPrimary={() => (
 *        <> ... existing tr contents ... </>
 *     )}
 *     renderVersionRow={(v) => (
 *        <> ... how to render one prior version ... </>
 *     )}
 *   />
 */
export function VersionAccordion({ record, kind, colSpan, renderPrimary, renderVersionRow,
                                   rowKey, rowClassName, primaryTestId }) {
  const [expanded, setExpanded] = useState(false);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  const versionCount = record?.version_count || 1;
  const hasAmendments = versionCount > 1;

  const toggle = async () => {
    if (!hasAmendments) return;
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (versions) return;
    setLoading(true);
    try {
      const r = await api.get(`/clinical-records/${kind}/${record.id}/versions`);
      setVersions(r.data.versions || []);
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  const openVersionRender = async (versionId) => {
    try {
      const params = versionId ? { version: versionId } : {};
      const r = await api.get(`/clinical-records/${kind}/${record.id}/render`,
        { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked. Allow pop-ups for this site.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <>
      <tr key={rowKey} data-testid={primaryTestId} className={rowClassName}>
        {renderPrimary({
          expandToggle: hasAmendments ? (
            <button type="button" onClick={toggle}
              data-testid={`version-toggle-${record.id}`}
              title={`${versionCount} versions. Click to see prior amendments.`}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#EDE7DC] text-[#4E342E] hover:bg-[#DBCDB6]">
              {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : null,
          versionBadge: hasAmendments ? (
            <span className="rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-bold text-[#8D5A00]">
              v{versionCount}
            </span>
          ) : null,
        })}
      </tr>
      {expanded && (
        <tr data-testid={`version-accordion-${record.id}`} className="bg-[#FDFCF8]">
          <td colSpan={colSpan} className="px-6 py-3">
            {loading ? (
              <div className="text-xs text-[#7B6157]">Loading version history…</div>
            ) : (
              <div className="space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#7B6157]">
                  Amendment history ({versionCount} versions - latest is above)
                </div>
                {(versions || []).map((v) => (
                  <div key={v.id}
                    data-testid={`version-row-${record.id}-v${v.version_number}`}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-[#EFEBE0] bg-white px-3 py-2 text-xs">
                    <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">
                      v{v.version_number}
                    </span>
                    <span className="text-[#7B6157]">{dayjs(v.created_at).format("MMM D, YYYY h:mm A")}</span>
                    {renderVersionRow ? renderVersionRow(v) : (
                      <span className="text-[#5D4037]">
                        <b>{v.editor_name}</b> ({v.editor_role}): {v.change_summary}
                      </span>
                    )}
                    <button type="button"
                      onClick={() => openVersionRender(v.id)}
                      data-testid={`version-view-${record.id}-v${v.version_number}`}
                      className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#4E342E] px-2.5 py-0.5 text-[10px] font-bold text-white hover:bg-[#3E2723]">
                      <FileText size={11} /> View v{v.version_number}
                    </button>
                  </div>
                ))}
                {versions && versions.length === 0 && (
                  <div className="text-xs italic text-[#7B6157]">No prior versions found.</div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
